import React, { useCallback, useEffect, useRef, useState } from 'react'

import DropdownButton from 'antd/es/dropdown/dropdown-button'
import { allBoardsSelector, currentBoardIdSelector } from 'features/boards/model/selectors/boardsSelectors'
import { downloadBoardsThunk } from 'features/boards/model/slice/boardsSlice'
import { actions, setIsAddNewBoardModalOpen } from 'features/common/model/slice/appSlice'
import { openModal } from 'features/common/model/slice/moadlSlice'
import { sharingActions } from 'features/common/model/slice/sharingSlice'
import { Resizer } from 'features/files-preview/ui/Resizer'
import { MemberRole } from 'features/team/model/types/members'
import { getUserRole, rootBoardIdSelector, userFamilyIdSelector } from 'features/user/model/selectors/userSelectors'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { ROUTES } from 'shared/constants/routesPaths'
import { SDK_TYPES } from 'shared/sdk/sdkConstants'
import { AppLogo } from 'shared/components/AppLogo'
import {
  ArrowRightOutlined, ArrowsAltOutlined,
  CaretDownFilled, CopyOutlined, DeleteOutlined, DownloadOutlined,
  EditOutlined, LinkOutlined, Menu,
  MenuProps,
  MoreOutlined,
  PlusCircleOutlined, SettingOutlined
} from 'shared/ui'
import { hasAccess } from 'shared/utils/access'
import styled from 'styled-components'
import classNames from 'classnames'
import { SidebarChatsList } from 'features/ai-assistant/ui/ChatsList'

import { SidebarTabsToggle } from './SidebarTabsToggle'
import EditableTeamName from './EditableTeamName'

interface SidebarProps {
  items: MenuProps['items'];
  boardsItems: any[];
  selectedKeys: string[];
  onAddBoard: () => void;
  teamName: string;
  onOpenBoard: (item: any) => void;
  onAssetDropOnBoard?: (assetIds: string[], boardId: string) => void;
  onBoardDropOnBoard?: (boardIds: string[], targetBoardId: string) => void;
}
interface MenuItemData {
  key?: React.Key;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  children?: MenuItemData[];
  type?: string;
}

export const Sidebar = ({
  items,
  boardsItems,
  selectedKeys,
  onAddBoard,
  onOpenBoard,
  teamName,
  onAssetDropOnBoard,
  onBoardDropOnBoard
}: SidebarProps) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({})
  const [dragOverBoardId, setDragOverBoardId] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const boards = useAppSelector(allBoardsSelector)
  const userRole = useAppSelector(getUserRole)
  const currentBoardId = useAppSelector(currentBoardIdSelector)
  const familyId = useAppSelector(userFamilyIdSelector)
  const location = useLocation()
  const rootBoardId = useAppSelector(rootBoardIdSelector)

  const isAiAssistant = location.pathname.startsWith(ROUTES.dashboard.aiAssistant)

  const sidebarWidthRef = useRef<number>(260)

  const findBoardById = (boardId: string) => {
    return boards.find(board => board.id === boardId) || null
  }

  useEffect(() => {
    const savedExpandedItems = localStorage.getItem(`sidebar-expanded-items-${teamName}`)
    if (savedExpandedItems) {
      setExpandedItems(JSON.parse(savedExpandedItems))
    }
  }, [teamName])

  useEffect(() => {
    localStorage.setItem(`sidebar-expanded-items-${teamName}`, JSON.stringify(expandedItems))
  }, [expandedItems, teamName])

  useEffect(() => {
    if (sidebarRef.current) {
      const savedWidth = localStorage.getItem('sidebarWidth')

      if (savedWidth) {
        sidebarWidthRef.current = Number(savedWidth)
        sidebarRef.current.style.setProperty('--sidebar-width', `${sidebarWidthRef.current}px`)
      }
    }
  }, [])

  const handleResize = useCallback((newWidth: number) => {
    sidebarWidthRef.current = newWidth

    if (sidebarRef.current) {
      sidebarRef.current.style.setProperty('--sidebar-width', `${newWidth}px`)
    }

    try {
      localStorage.setItem('sidebarWidth', String(newWidth))
    } catch (e) {
      console.error('Failed to save sidebar width to localStorage:', e)
    }
  }, [])

  const toggleExpand = (e: React.MouseEvent, itemKey: string) => {
    e.stopPropagation()
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }))
  }

  const handleAddBoard = (e: React.MouseEvent, itemKey: string) => {
    e.stopPropagation()
    dispatch(actions.setModalBoardData({ boardIds: null, boards: null, parentId: itemKey }))
    dispatch(setIsAddNewBoardModalOpen(true))
  }

  const handleSettingsClick = () => {
    navigate(ROUTES.profile.manageMembers)
  }

  const handleDragOver = (e: React.DragEvent, boardId: string) => {
    e.preventDefault()

    try {
      const dragData = e.dataTransfer.getData('application/json')
      if (dragData) {
        const { id, boards } = JSON.parse(dragData)

        if (boards && boards.includes(boardId) || id === boardId) {
          e.dataTransfer.dropEffect = 'none'
          return
        }
      }
    } catch (error) {
    }

    e.dataTransfer.dropEffect = 'move'
    setDragOverBoardId(boardId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverBoardId(null)
    }
  }

  const handleDrop = (e: React.DragEvent, boardId: string) => {
    e.preventDefault()
    setDragOverBoardId(null)

    try {
      const dragData = e.dataTransfer.getData('application/json')
      if (dragData) {
        const { id, assets, boards } = JSON.parse(dragData)

        if (boards && boards.includes(boardId) || id === boardId) {
          return
        }

        if (assets && assets.length > 0 && onAssetDropOnBoard) {
          onAssetDropOnBoard(assets, boardId)
        }

        if (boards && boards.length > 0 && onBoardDropOnBoard) {
          onBoardDropOnBoard(boards, boardId)
        }
      }
    } catch (error) {
      console.error('Error parsing drag data:', error)
    }
  }

  const menuItems = [
    {
      key: 'open',
      label: 'Open',
      icon: <ArrowsAltOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: 'share',
      label: 'Share link',
      icon: <LinkOutlined />,
    },
    // {
    //   key: 'make_favorite',
    //   label: 'Make favorite',
    //   icon: <StarOutlined />,
    // },
    {
      type: 'divider',
    },
    {
      key: 'download',
      label: 'Download',
      icon: <DownloadOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: <CopyOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardToolbarAction.Move,
      label: 'Move to board',
      icon: <ArrowRightOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardToolbarAction.Rename,
      label: 'Rename',
      icon: <EditOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      type: 'divider',
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      key: BoardToolbarAction.Delete,
      label: 'Delete from library',
      icon: <DeleteOutlined />,
      danger: true,
    }
  ]

  const currentBoard = findBoardById(currentBoardId)

  const handleActionClick = (boardId: string, actionId: string) => {

    switch (actionId) {
    case BoardToolbarAction.Open:
      navigate(`${ROUTES.dashboard.board}/${boardId}`)
      break
    case BoardToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: 'board', data: { ids: [boardId], items: [findBoardById(boardId)] } }))
      break
    case BoardToolbarAction.Copy:
      dispatch(openModal({ type: BoardToolbarAction.Copy, context: 'board', data: { ids: [boardId], items: [findBoardById(boardId)] } }))
      break
    case BoardToolbarAction.Move:
      dispatch(openModal({ type: BoardToolbarAction.Move, context: 'board', data: { ids: [boardId], items: [findBoardById(boardId)] } }))
      break
    case BoardToolbarAction.Delete:
      dispatch(openModal({
        type: BoardToolbarAction.Delete, context: 'board', data: {
          ids: [boardId],
          items: [findBoardById(boardId)],
          redirectTo: currentBoardId === boardId ? currentBoard?.parent_id === rootBoardId ? ROUTES.dashboard.allAssets : `${ROUTES.dashboard.board}/${currentBoard?.parent_id}` : null
        }
      }))
      break
    case BoardToolbarAction.Download:
      dispatch(downloadBoardsThunk({ ids: [boardId] }))
      break
    case BoardToolbarAction.Share:
      dispatch(openModal({ type: BoardToolbarAction.Share, context: 'board', data: { ids: [boardId], items: [findBoardById(boardId)] } }))
      dispatch(sharingActions.setSharingAlbumConfig({
        sdkType: SDK_TYPES.FILES
      }))
      dispatch(sharingActions.setSharedItemData(findBoardById(boardId)))
      break
    }
  }

  const getMenuProps = (boardId: string) => {
    return {
      items: menuItems as any,
      onClick: (e) => {
        e.domEvent.stopPropagation()
        handleActionClick(boardId, e.key)
      },
    }
  }

  const renderMenuItem = (menuItem: MenuItemData, level = 1) => {
    if (!menuItem || menuItem.type === 'divider' || !menuItem.key) {
      return null
    }

    const itemKey = String(menuItem.key)
    const hasChildren = menuItem.children && menuItem.children.length > 0
    const isExpanded = expandedItems[itemKey]
    const isDragOver = dragOverBoardId === itemKey

    return (
      <React.Fragment key={itemKey}>
        <SCustomMenuItem
          className={classNames({
            'active-menu-item': selectedKeys[0] === itemKey,
            'drag-over': isDragOver
          })}
          level={level}
          onClick={() => onOpenBoard({ id: itemKey })}
          onDragOver={(e) => handleDragOver(e, itemKey)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, itemKey)}
        >
          <div className={`menu-left-part`}>

            {hasChildren && (
              <div

                className="expand-icon"
                onClick={(e) => toggleExpand(e, itemKey)}
              >
                <CaretDownFilled className={isExpanded ? "icon-normal" : "icon-rotate"  } />
              </div>
            )}

            <span className="menu-item-label">
              {menuItem.label}
            </span>
          </div>

          <div className="menu-item-actions">
            <DropdownButton
              menu={getMenuProps(itemKey)}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
              buttonsRender={() => (
                [
                  null,
                  <MoreOutlined
                    key={itemKey}
                    className="action-icon"
                  />
                ]
              )}
            />

            {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && (
              <PlusCircleOutlined
                className="action-icon"
                onClick={(e) => handleAddBoard(e, itemKey)}
              />
            )}
          </div>
        </SCustomMenuItem>

        {hasChildren && (
          <SNestedMenu className={isExpanded ? "expanded" : ""}>
            {menuItem.children.map(childItem => renderMenuItem(childItem, level + 1))}
          </SNestedMenu>
        )}
      </React.Fragment>
    )
  }

  const renderBoardItems = () => {
    if (!boardsItems) return null
    return boardsItems.map(item => renderMenuItem(item as MenuItemData))
  }

  return (
    <SSidebarContainer ref={sidebarRef}>
      <Resizer onResize={handleResize}
        minWidth={260}
        maxWidth={500}
        direction="left"
      />

      <SCompanyHeader>
        <LogoBox>
          <AppLogo
            variant="only"
            width={26}
            height={26}
          />

          <EditableTeamName teamName={teamName} />

        </LogoBox>

        {/* <SMenuFoldIcon /> */}
      </SCompanyHeader>

      <SSidebarTabsToggle />

      {!isAiAssistant && (
        <>
          <SMenu
            inlineIndent={24}
            mode="inline"
            items={items}
            selectedKeys={selectedKeys}
          />

          <SBoardsTitleContainer>
          Boards
            {hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && (
              <SAddBoardIcon onClick={onAddBoard} />
            )}
          </SBoardsTitleContainer>

          <SCustomBoardsMenu>
            {renderBoardItems()}
          </SCustomBoardsMenu>
        </>
      )}

      {isAiAssistant && (
        <SidebarChatsList />
      )}

      <SBottomActions>
        <SNotificationSettingsRow onClick={handleSettingsClick}>
          <SSettingOutlined className="settings-icon" />

          <SSettingsText>
Settings
          </SSettingsText>
        </SNotificationSettingsRow>
      </SBottomActions>
    </SSidebarContainer>
  )
}

const SSidebarContainer = styled.div`
  --sidebar-width: 260px;
  width: var(--sidebar-width, 260px);
  background-color: #F5F5F5;
  display: flex;
  flex-direction: column;
  overflow: auto;
  position: relative;
`

const SCompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 19px 12px 19px 15px;
  justify-content: space-between;
`





const SMenu = styled(Menu)`
  border-inline-end: none !important;
  background-color: #F5F5F5;
  &.ant-menu{
    padding-top: 5px;
  }

  .ant-menu-item {
    height: 40px;
    line-height: 40px;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.65);
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: "SF Pro Text";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    margin: 2px 4px 0;

    &.ant-menu-item-selected {
      background-color: rgba(0, 0, 0, 0.04);

      &:active {
        background-color: rgba(0, 0, 0, 0.15) !important;
      }
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.15) !important;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }
`

const SCustomBoardsMenu = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: calc(100vh - 468px);
  overflow: auto;

  @supports (-moz-appearance: none) { 
    scrollbar-color: var(--scroll-color) transparent; 
  }
  
  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const SNestedMenu = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.2s ease-in;
  
  &.expanded {
    max-height: 1000px;
    overflow: visible;
  }
`

interface SCustomMenuItemProps {
  level: number;
}

const SCustomMenuItem = styled.div<SCustomMenuItemProps>`
  min-height: 40px;
  height: 40px;
  display: flex;
  padding: 0px 15px 0px ${props => 23 * props.level}px;
  margin: 2px 4px 0;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.65);
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  border-radius: 8px;
  border: 2px solid transparent;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &.active-menu-item {
    background-color: rgba(0, 0, 0, 0.04);

    /* .menu-item-actions {
    display: flex;
  } */
  }

  &.drag-over {
    transition: unset;
    border: 2px solid #6532B8;
  }
  
  .menu-left-part {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
  }
  
  .menu-item-label {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  
  .menu-item-actions {
    display: none;
    gap: 6px;
    transition: display 0.4s;
    flex-shrink: 0;
    overflow: visible;
  }
  
  .action-icon {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
    
    &:hover {
      color: var(--color-primary);
    }
  }
  
  &:hover .menu-item-actions {
    display: flex;
  }
  
  &:hover .menu-left-part {
    max-width: calc(100% - 60px);
  }
  
  .expand-icon {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    
    .icon-rotate {
      transition: transform 0.2s;
      transform: rotate(-90deg);
    }
    
    .icon-normal {
      transition: transform 0.2s;
      transform: rotate(0);
    }
  }
`

const SBoardsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  padding: 24px 15px 8px 22px;
  margin: 0 4px;
`

const SAddBoardIcon = styled(PlusCircleOutlined)`
  font-size: 18px;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: var(--color-primary);
  }
`

const SBottomActions = styled.div`
  position: absolute;
  bottom: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const SSettingOutlined = styled(SettingOutlined)`
  font-size: 18px;
  color: rgba(0, 0, 0, 0.45);
`

const SSettingsText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: inherit;
`

const SNotificationSettingsRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 20px 0 24px;
  gap: 10px;
  cursor: pointer;
  border-radius: 8px;
  height: 40px;
  line-height: 40px;
  margin: 2px 4px 0;
  color: rgba(0, 0, 0, 0.65);
  transition: background-color, color 0.3s;

  &:hover {
    ${SSettingOutlined} {
      color: inherit;
    }
    color: rgba(0, 0, 0, 0.88);
    background-color: rgba(0, 0, 0, 0.08);
  }
`

const LogoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SSidebarTabsToggle = styled(SidebarTabsToggle)`
  margin: 8px 4px 12px;
`

export default Sidebar
